<template>
  <div
    class="m-3"
  >
    <div class="d-flex align-items-center justify-content-between mb-3">
      <label class="mb-0">
        <i class="far fa-file pr-1" />
        {{ $t('menu.import') }}
      </label>

      <div @click="$emit('close')">
        <i class="fas fa-times secondary-icon" />
      </div>
    </div>
    <div
      class="flex-grow-1 import-scroll"
    >
      <div
        v-if="validationError"
        class="text-center mb-3"
      >
        <div class="d-flex align-items-center text-danger justify-content-center">
          <i class="fa-solid fa-triangle-exclamation fa-2x mr-2" />
          {{ $t('import.validationError') }}
        </div>
      </div>

      <FileUpload
        v-if="!importFile"
        class="upload h-100 mt-1 d-flex align-items-center justify-content-center"
        @upload="validateFile"
      />
      <div
        v-if="importPending"
        class="text-center"
      >
        <Loader />
      </div>

      <div v-if="fileValidation">
        <hr>
        <div class="d-flex justify-content-between">
          <div />
          <div>
            <button
              class="btn btn-sm btn-primary"
              :disabled="importPending || blockingErrors.length > 0"
              @click="upload"
            >
              {{ $t('shop.confirmImport') }}
              <i class="fa-regular fa-arrow-right pl-2" />
            </button>
          </div>
        </div>
        <hr>

        <template v-if="blockingErrors.length > 0">
          <div class="font-weight-bold">
            {{ $t('import.errors') }}
          </div>
          <div class="small text-danger">
            {{ $t('import.fixErrors') }}
          </div>
          <div
            v-for="(error, i) in blockingErrors"
            :key="`error_${i}`"
          >
            <ShopImportError
              v-bind="error"
              danger
            />
          </div>

          <hr>
        </template>
        <template v-if="warnings.length > 0">
          <div class="font-weight-bold">
            {{ $t('import.warnings') }}
          </div>
          <div
            v-for="(error, i) in warnings"
            :key="`warning_${i}`"
          >
            <ShopImportError v-bind="error" />
          </div>

          <hr>
        </template>
        <template v-if="added.length > 0">
          <div class="font-weight-bold">
            {{ $t('import.new') }}
          </div>

          <ListingsList
            :listings="added"
            hide-checkbox
            create
          />
          <hr>
        </template>
        <template v-if="updated.length > 0">
          <div class="font-weight-bold">
            {{ $t('import.updated') }}
          </div>
          <ListingsList
            :listings="updated"
            hide-checkbox
            update
          />
          <hr>
        </template>
        <template v-if="unchanged.length > 0">
          <div class="font-weight-bold">
            {{ $t('import.unchanged') }}
          </div>
          <ListingsList
            style="opacity: 0.7"
            :listings="unchanged"
            hide-checkbox
          />
          <hr>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import FileUpload from '@/components/FileUpload';
import ShopImportError from '@/components/shop/ShopImportError';
import { mapActions, mapGetters } from 'vuex';
import ListingsList from './ListingsList';
import { weekDayIndex } from './timetable';

export default {
  props: {
    catalogId: String,
    selectedNodes: Array,
  },
  data: () => ({
    fileValidation: null,
    importFile: null,
    importPending: false,
    validationError: false,
    // current timetables, loaded when the file has a timetable column
    calendarRules: [],
  }),
  components: {
    FileUpload,
    ListingsList,
    ShopImportError,
  },
  computed: {
    ...mapGetters('shop', [
      'listings',
      'catalogDetails',
    ]),
    blockingErrors() {
      return (this.fileValidation?.errors || []).filter((x) => !x.warning);
    },
    warnings() {
      return (this.fileValidation?.errors || []).filter((x) => x.warning);
    },
    importedListings() {
      if (!this.fileValidation) return [];
      return this.fileValidation.listings
        .map((x) => ({ ...x, importTimetable: this.fileValidation.hasTimetable }));
    },
    added() {
      return this.importedListings
        .filter((x) => x.id === '00000000-0000-0000-0000-000000000000');
    },
    updated() {
      return this.importedListings
        .filter((x) => x.id !== '00000000-0000-0000-0000-000000000000')
        .map((x) => {
          const ref = this.listings.find((l) => l.id === x.id);

          const fields = [];

          if (ref.name !== x.name) {
            fields.push('name');
          }
          if (ref.description !== x.description) {
            fields.push('description');
          }
          if (ref.orderFrom !== x.orderFrom) {
            fields.push('orderFrom');
          }
          if (ref.orderTo !== x.orderTo) {
            fields.push('orderTo');
          }
          if (ref.cancelTo !== x.cancelTo) {
            fields.push('cancelTo');
          }
          if (ref.countLimit !== x.countLimit) {
            fields.push('countLimit');
          }
          if (ref.subscription?.enabled !== x.subscription?.enabled
          || ref.subscription?.applyAt !== x.subscription?.applyAt) {
            fields.push('subscription');
          }
          if (ref.price !== x.price || ref.currency !== x.currency) {
            fields.push('price');
          }

          if (this.fileValidation.hasTimetable) {
            const current = this.calendarRules.filter((r) => r.offerId === x.id);
            if (this.timetableKey(current) !== this.timetableKey(x.calendarRules)) {
              fields.push('timetable');
            }
          }

          if (ref.productIds.length !== x.productIds.length
            || !ref.productIds.every((id) => x.productIds.includes(id))
            || !x.productIds.every((id) => ref.productIds.includes(id))) {
            fields.push('productIds');
          }

          this.catalogDetails.subCatalogs.forEach((sc) => {
            const old = ref.assignments
              .filter((a) => a.subCatalogId === sc.subCatalogId)
              .map((a) => a.nodeId)
              .sort((a, b) => a.localeCompare(b))
              .join(';');
            const up = x.assignments
              .filter((a) => a.subCatalogId === sc.subCatalogId)
              .map((a) => a.nodeId)
              .sort((a, b) => a.localeCompare(b))
              .join(';');

            if (old !== up) {
              fields.push(sc.subCatalogId);
            }
          });

          this.catalogDetails.listingFields.forEach((lf) => {
            if (ref.fields[lf.fieldId] !== x.fields[lf.fieldId]) {
              fields.push(lf.fieldId);
            }
          });

          return {
            ...x,
            modifiedFields: fields,
          };
        })
        .filter((x) => x.modifiedFields.length > 0);
    },
    unchanged() {
      const updatedIds = this.updated.map((x) => x.id);
      return this.importedListings
        .filter((x) => x.id !== '00000000-0000-0000-0000-000000000000' && !updatedIds.includes(x.id));
    },
  },
  methods: {
    ...mapActions('shop', [
      'offerImportValidate',
      'offerImport',
      'getCalendarRules',
    ]),
    timetableKey(rules) {
      return (rules || [])
        .map((r) => `${weekDayIndex(r.weekDay)}|${r.startMinutes}|${r.durationMinutes}`)
        .sort()
        .join(';');
    },
    validateFile(file) {
      this.validationError = false;
      const formData = new FormData();
      formData.append('file', file);

      for (let i = 0; i < this.selectedNodes; i += 1) {
        formData.append(`nodeIds[${i}]`, this.selectedNodes[i].code);
      }

      this.importFile = file;
      this.importPending = true;

      this.offerImportValidate({
        params: {
          catalogId: this.catalogId,
        },
        data: formData,
      })
        .then(async ({ data }) => {
          if (data.hasTimetable) {
            const { data: rules } = await this.getCalendarRules({ params: { catalogId: this.catalogId } });
            this.calendarRules = rules;
          }
          this.fileValidation = data;
        })
        .catch(() => {
          this.validationError = true;
          this.importFile = null;
        })
        .finally(() => {
          this.importPending = false;
        });
    },
    upload() {
      const formData = new FormData();
      formData.append('file', this.importFile);

      for (let i = 0; i < this.selectedNodes; i += 1) {
        formData.append(`nodeIds[${i}]`, this.selectedNodes[i].code);
      }

      this.importPending = true;

      this.offerImport({
        params: {
          catalogId: this.catalogId,
        },
        data: formData,
      })
        .then(() => {
          this.$emit('close');
        })
        .catch((err) => {
          // import rejected by validation - show the reasons, nothing was saved
          const errors = err?.response?.data?.errors;
          if (errors) {
            this.fileValidation = { ...this.fileValidation, errors };
          } else {
            this.validationError = true;
          }
        })
        .finally(() => {
          this.importPending = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.import-scroll {
  max-height: calc(100vh - 320px);
  overflow-y: auto;
}
</style>
